import Layer from "./Layer";
import {fabric} from 'fabric';
import LayerManager from "./LayerManager";
import { BehaviorSubject } from "rxjs";
import ImageLayer from "./ImageLayer";
import { SerializedSVGLayerInfo } from "./Interfaces";
import Fill from "./Fill";

export default class SVGLayer extends Layer {

    public $pattern = new BehaviorSubject<undefined | string | fabric.Pattern | fabric.Gradient>(undefined);
    public fillObservable = this.$pattern.asObservable();
    public fill!: Fill;
    public url: string;
    public borderStyle:'solid' | 'dashed'

    constructor(obj: fabric.Object, layerManager:LayerManager, name?: string) {
        super(obj, layerManager, name);
        this.fill = new Fill(obj.height,obj.width);
        this.pattern = this.fill.redraw();
        this.setAspectRatioLock(true);
        this.type = "svg";
    }

    public set pattern(value: string | fabric.Pattern | fabric.Gradient) {
        this.emit("BEFORE_MODIFY", this.toJSON());
        this.fabricRef.set("fill",value);
        this.$pattern.next(value);
        this.fabricRef.canvas?.renderAll();
        this.fabricRef.canvas?.fire("object:modified", { target: this.fabricRef });
    }

    public get pattern() {
        return this.fabricRef.get("fill");
    }
    
    public async setFillFromImageLayer(imageLayer: ImageLayer) {
        console.log('tag svg', this.fill);
        let imageElement = document.createElement("img");
        imageElement.crossOrigin="anonymous";
        let onLoadPromise = new Promise(res=>{
            imageElement.onload = (e) => {
                res(e);
            }
        })
        imageElement.src = imageLayer.src;
        imageElement.height = imageLayer._dimensions.value.height;
        imageElement.width = imageLayer._dimensions.value.width;
        await onLoadPromise;
        this.fill.setBackgroundImage(imageElement);
        this.pattern = this.fill.redraw();
    }

    public set backgroundColor(color:string) {
        this.fill.backgroundColor = color;
        this.pattern = this.fill.redraw();
    }

    public get backgroundColor() {
        return this.fill.backgroundColor;
    }
    
    public set backgroundImageZoom(zoomValue: number) {
        this.fill.backgroundImageZoom = zoomValue;
        this.pattern = this.fill.redraw();
    }

    public get backgroundImageZoom() {
        return this.fill.backgroundImageZoom;
    }

    public get backgroundPositionX() {
        return this.fill.positionX;
    }

    public set backgroundPositionX(value:number) {
        this.fill.positionX = value;
        this.pattern = this.fill.redraw();
    }

    public get backgroundPositionY() {
        return this.fill.positionY;
    }

    public set backgroundPositionY(value:number) {
        this.fill.positionY = value;
        this.pattern = this.fill.redraw();
    }

    public get backgroundOpacity() {
        return this.fill.imageOpacity;
    }

    public set backgroundOpacity(value: number) {
        this.fill.imageOpacity = value;
        this.pattern = this.fill.redraw();
    }

    public get borderThickness() {
        
        return this.fabricRef.strokeWidth;
    }

    public set borderThickness(value: number) {
        this.emit("BEFORE_MODIFY", this.toJSON());
        
        try {
          this.fabricRef.set("dirty", true);
          this.fabricRef.strokeWidth = value;
          this.fabricRef.canvas?.renderAll();
        } catch (e) {
          console.error(e);
        }
        
        this.fabricRef.canvas?.fire("object:modified", { target: this.fabricRef });
        this.emit("MODIFIED", this.toJSON());
    }      

    public get borderColor() {
        return this.fabricRef.stroke;
    }

    public set borderColor(value: string) {
        this.emit("BEFORE_MODIFY", this.toJSON());
        
        try {
          this.fabricRef.set("stroke", value);
          this.fabricRef.canvas?.renderAll();
        } catch (e) {
          console.error(e);
        }
        
        this.fabricRef.canvas?.fire("object:modified", { target: this.fabricRef });
        this.emit("MODIFIED", this.toJSON());
    }      

    public set backgroundBlury(value:boolean) {
        this.fill.backgroundBlury = value;
        this.pattern = this.fill.redraw();
    }

    public get backgroundBlury() {
        return this.fill.backgroundBlury;
    }

    public set strokeDashArray(dashValue: Array<number> | []) {
        this.emit("BEFORE_MODIFY", this.toJSON());
        
        try {
          this.fabricRef.set('strokeDashArray', dashValue);
          this.fabricRef.canvas?.renderAll();
        } catch (e) {
          console.error(e);
        }
        
        this.fabricRef.canvas?.fire("object:modified", { target: this.fabricRef });
        this.emit("MODIFIED", this.toJSON());
    }
      
    public get strokeDashArray() {
        return this.fabricRef.strokeDashArray;
    }      

    public setBorderStyle(style: 'solid' | 'dashed' = 'solid', strokeDashArrayParam: Array<number>) {
        this.emit("BEFORE_MODIFY", this.toJSON());
        
        try {
          const strokeDashArray = style === 'dashed' ? strokeDashArrayParam : [];
          this.fabricRef.set({ strokeDashArray });
          this.borderStyle = style;
          this.strokeDashArray = style === 'dashed' ? strokeDashArrayParam : [];
          this.fabricRef.canvas?.renderAll();
        } catch (e) {
          console.error(e);
        }
        
        this.fabricRef.canvas?.fire("object:modified", { target: this.fabricRef });
        this.emit("MODIFIED", this.toJSON());
    }

    public override async setPropertiesFromSerializedData(data: SerializedSVGLayerInfo) {
        super.setPropertiesFromSerializedData(data);
        await this.fill.enliven(data.fill);

        // Set properties of SVGLayer
        this.borderColor = data.borderColor;
        this.borderStyle = data.borderStyle;
        this.borderThickness = Number(data.borderThickness) || 0;
        this.strokeDashArray = data.strokeDashArray;
        this.pattern = this.fill.redraw();
        this.fabricRef.canvas.renderAll();
    }

    public toJSON(): SerializedSVGLayerInfo {
        let serializedLayer = super.toJSON();
        let serializedSVGLayer:SerializedSVGLayerInfo = {
            ...serializedLayer,
            fill: this.fill.toJSON(),
            borderColor: this.borderColor,
            borderStyle: this.borderStyle,
            borderThickness: this.borderThickness,
            strokeDashArray: this.strokeDashArray,
            url:this.url
        }
        return serializedSVGLayer;
    }    
}
